import { Directive, ElementRef, inject, NgZone, OnDestroy, OnInit, output, OutputEmitterRef } from '@angular/core';
import { WindowRefService } from '@services/window-ref-service';

const KEYBOARD_OPEN_THRESHOLD_PX = 120;
const VIEWPORT_SETTLE_MS = 50;
const FOCUS_SCROLL_DELAY_MS = 300;

/**
 * Publishes the on-screen keyboard height as `--cureskin-keyboard-inset` on the host element, and
 * keeps the focused input scrolled clear of the keyboard.
 *
 * Not a duplicate of `KeyboardVisibilityDirective`. See the "Keyboard handling" section of
 * `client/app/onboarding/onboarding-profile/CLAUDE.md` before changing this or adding a third.
 */
@Directive({
  selector: '[appKeyboardInset]',
})
export class KeyboardInsetDirective implements OnInit, OnDestroy {
  /** Fires only on transitions, so bindings settle instead of thrashing per animation frame. */
  readonly keyboardOpenChange: OutputEmitterRef<boolean> = output<boolean>();

  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly windowRef: WindowRefService = inject(WindowRefService);
  private readonly zone: NgZone = inject(NgZone);

  private keyboardOpen: boolean = false;
  private viewport: VisualViewport | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;
  private focusScrollTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onViewportChange = (): void => this.scheduleMeasure();
  private readonly onFocusIn = (event: FocusEvent): void => this.handleFocusIn(event);

  ngOnInit(): void {
    const nativeWindow = this.windowRef.nativeWindow;
    this.viewport = nativeWindow?.visualViewport ?? null;
    if (!this.viewport) return;

    this.zone.runOutsideAngular((): void => {
      this.viewport?.addEventListener('resize', this.onViewportChange);
      this.viewport?.addEventListener('scroll', this.onViewportChange);
      this.elementRef.nativeElement.addEventListener('focusin', this.onFocusIn);
    });
    this.measure();
  }

  ngOnDestroy(): void {
    this.viewport?.removeEventListener('resize', this.onViewportChange);
    this.viewport?.removeEventListener('scroll', this.onViewportChange);
    this.elementRef.nativeElement.removeEventListener('focusin', this.onFocusIn);
    if (this.settleTimer) clearTimeout(this.settleTimer);
    if (this.focusScrollTimer) clearTimeout(this.focusScrollTimer);
    this.setInset(0);
  }

  private scheduleMeasure(): void {
    if (this.settleTimer) clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout((): void => this.measure(), VIEWPORT_SETTLE_MS);
  }

  private measure(): void {
    const nativeWindow = this.windowRef.nativeWindow;
    if (!this.viewport || !nativeWindow) return;
    const hiddenHeight = nativeWindow.innerHeight - (this.viewport.height + this.viewport.offsetTop);
    const isOpen = hiddenHeight > KEYBOARD_OPEN_THRESHOLD_PX;
    this.setInset(isOpen ? Math.round(hiddenHeight) : 0);
    if (isOpen === this.keyboardOpen) return;
    this.keyboardOpen = isOpen;
    this.zone.run((): void => this.keyboardOpenChange.emit(isOpen));
  }

  private setInset(pixels: number): void {
    this.elementRef.nativeElement.style.setProperty('--cureskin-keyboard-inset', `${pixels}px`);
  }

  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement | null;
    if (!target || !(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)) return;
    if (this.focusScrollTimer) clearTimeout(this.focusScrollTimer);
    this.focusScrollTimer = setTimeout((): void => {
      this.measure();
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, FOCUS_SCROLL_DELAY_MS);
  }
}
